// `entity-checkbox.resolver.ng1.ts` — the shortlist's checked-entity container.
//
// ⚠️ UPSTREAM MAKES THIS A SERVICE, AND THE REASON IS NOT SHARING — IT IS LIFETIME. The port
// held the selection in `ShortlistReview`'s own `useState`, which reads as the simpler choice
// and is wrong for a measured reason: **entering a side-view child route unmounts and remounts
// the review screen.** Instrumented on 15 Oct against the built SPA at
// `https://extension.dev.woo.io`, a click on the bar's Add-to-job logs
//
//     ShortlistReview UNMOUNTED → ShortlistReview MOUNTED → per-alert reset fired
//
// and the list refetches (`getAlertsWithExtraDetails` and `getCandidatesByAlertId` fire again).
// So every bulk action opened its panel with an empty selection: `addToAtsJobAsync` posted a
// well-formed request for ZERO candidates, which the server accepts, and the whole thing was a
// silent no-op against a real ATS rather than an error anyone could see.
//
// Holding it outside the component tree makes the remount irrelevant, which is exactly what
// the resolver does upstream — `entityCheckboxContainer` is injected into both the list and
// each panel, and neither owns it.
//
// ⚠️ KEYED BY ALERT, WHICH REPLACES THE RESET. The old code cleared the selection in the
// per-alert effect ("moving to another alert drops it"). That effect re-runs on every remount,
// so keeping it here would re-create the bug this file exists to fix. Namespacing by alert id
// gives the same guarantee structurally: alert 71 simply never sees alert 70's set.

/** What a panel needs about a checked row: upstream's `checkedEntities` carries both. */
export interface CheckedEntity {
  id: number
  name?: string | null
}

type Listener = () => void

const byAlert = new Map<number, readonly CheckedEntity[]>()
const listeners = new Set<Listener>()

/**
 * ⚠️ THE SNAPSHOT IS A NUMBER, NOT THE SELECTION. `useSyncExternalStore` needs a snapshot
 * whose identity is stable between unrelated renders, and the selection a caller wants depends
 * on which alert it is looking at — which the caller resolves asynchronously. Versioning the
 * whole store lets a component subscribe before it knows its alert and still read the right
 * set once it does, with no ref to keep in sync.
 */
let version = 0

function announce(): void {
  version += 1
  for (const listener of listeners) listener()
}

export function getCheckedVersion(): number {
  return version
}

/**
 * ⚠️ ONE FROZEN INSTANCE, NOT A FRESH `[]`. `useSyncExternalStore` compares snapshots by
 * identity and re-renders when they differ, so returning a new empty array per call is an
 * infinite render loop rather than an empty selection.
 */
const EMPTY: readonly CheckedEntity[] = Object.freeze([])

export function getCheckedEntities(alertId: number | undefined): readonly CheckedEntity[] {
  if (alertId === undefined) return EMPTY
  return byAlert.get(alertId) ?? EMPTY
}

export function setCheckedEntities(alertId: number, next: readonly CheckedEntity[]): void {
  if (next.length === 0) byAlert.delete(alertId)
  else byAlert.set(alertId, next)
  announce()
}

/** `resetCandidatesCheckbox` — what Clear all, and a completed bulk write, both call. */
export function clearCheckedEntities(alertId: number | undefined): void {
  if (alertId === undefined) return
  if (!byAlert.delete(alertId)) return
  announce()
}

export function subscribeToCheckedEntities(listener: Listener): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/** Test seam: the store outlives a component, so a test that writes it must undo that. */
export function resetAllCheckedEntities(): void {
  byAlert.clear()
  announce()
}
